import React from 'react';

import cx from 'classnames';
import { showModal } from 'modal.react.js';
import { useFormContext } from 'react-hook-form';

import { useFetchCountries } from 'components/Pages/Admin/Itineraries/shared/hooks/useFetchCountries';
import { useFetchPortfolios } from 'components/Pages/Admin/Itineraries/shared/hooks/useFetchPortfolios';
import Preview from 'components/Pages/Admin/shared/Preview';
import { FormFieldWrapper } from 'components/shared/BaseFormComponents';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { Select } from 'components/shared/Select/adapters/HookForm';
import { SafariContentOption } from 'components/shared/Select/options/SafariContent';
import Text from 'components/shared/Typography/Text';
import { isNotDefined } from 'utils/ramda';

import sharedStyles from '../../../../../shared/index.module.css';
import { useFetchCountyAreas } from '../../../../useFetchCountyAreas';
import { GoogleMapsAdapter, NameSection, TagsSection } from '../../../shared';
import * as types from '../../../types';
import { PropertyFormProps } from './types';
import { useFetchMasterProperty } from './useFetchMasterProperty';

function PropertyForm({ isReadOnly, isAgent }: PropertyFormProps) {
  const methods = useFormContext<types.PropertyFormValues>();
  const { control, watch, formState } = methods;
  const countriesQueryRes = useFetchCountries();
  const handledCountries =
    countriesQueryRes.data?.data.map((item) => ({ value: item.id.toString(), label: item.name })) || [];

  const countryId = watch('countryId');
  const { countryAreasQueryRes, handledAreas } = useFetchCountyAreas(countryId);

  React.useEffect(() => {
    if (countryId && formState.isDirty) methods.setValue('area', null);
  }, [countryId]);

  const { masterPropertyQuery } = useFetchMasterProperty();

  const portfoliosQueryRes = useFetchPortfolios();
  const handledPortfolios = portfoliosQueryRes.data?.map((item) => ({ value: item, label: item })) || [];

  const openPagePreview = async (pageId: number) => {
    await showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} />
      </Modal>
    ));
  };

  const [lockMenu, setLockMenu] = React.useState(false);
  const ActivityOptionMemoized = React.useCallback((props: any) => {
    return (
      <SafariContentOption
        {...props}
        hasPreview={!!props.data.optionActionAvailable}
        onPreview={async () => {
          if (!openPagePreview) return;
          setLockMenu(true);
          await openPagePreview(props.data.pageId);
          setLockMenu(false);
        }}
        hasContentIndicator={props.data.showContentIndicator}
        shared={props.data?.shared}
        subtitle={props.data.tagsString}
      />
    );
  }, []);

  return (
    <div>
      <div className={sharedStyles.sectionContainer}>
        <NameSection control={control}>Property Details</NameSection>

        <div className={sharedStyles.adapterContainer}>
          <div className={sharedStyles.gridFieldContainer}>
            <div className={sharedStyles.gridSelectorContainer}>
              <Select
                name="countryId"
                control={control}
                label={() => 'Select Country*'}
                options={handledCountries}
                getOptionLabel={(option) => option?.label}
                getOptionValue={(option) => option?.value}
                isLoading={countriesQueryRes.isLoading}
                optionToString
                isClearable
                blurInputOnSelect
              />
            </div>

            <div className={sharedStyles.gridSelectorContainer}>
              <FormFieldWrapper errorMessage={formState.errors.area?.name?.message}>
                <Select
                  key={countryId}
                  name="area"
                  control={control}
                  label={() => 'Select Area*'}
                  options={handledAreas}
                  isDisabled={isNotDefined(countryId)}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  isLoading={countryAreasQueryRes.isFetching}
                  blurInputOnSelect
                  lockMenu={lockMenu}
                  components={{ Option: ActivityOptionMemoized }}
                  isClearable
                  creatable
                  onCreateOption={(inputValue) => ({ name: inputValue, id: null, confirmed: false }) as any}
                />
              </FormFieldWrapper>
            </div>
          </div>
        </div>

        <div className={cx(sharedStyles.headlineContainer, sharedStyles.descriptionContainer)}>
          <Text type="brandon" className={sharedStyles.descriptionText}>
            Link To Master Property Page{isAgent ? '*' : ''}
          </Text>
        </div>

        <FormFieldWrapper errorMessage={formState.errors.masterProperty?.name?.message}>
          {/* ts can not understand that "creatable" and "onCreateOption" props are synchronized */}
          {/* @ts-expect-error */}
          <Select
            isDisabled={isReadOnly}
            name="masterProperty"
            control={control}
            label={() => `Select Master Property${isAgent ? '*' : ''}`}
            async
            loadOptions={masterPropertyQuery}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.id?.toString() || ''}
            isLoading={countriesQueryRes.isLoading}
            blurInputOnSelect
            editable
            isClearable={!isAgent}
            creatable={isAgent}
            onCreateOption={isAgent ? (inputValue) => ({ name: inputValue, id: null }) as any : undefined}
            required={isAgent}
            lockMenu={lockMenu}
            components={{
              Option: ActivityOptionMemoized,
            }}
          />
        </FormFieldWrapper>
      </div>

      <TagsSection methods={methods} />

      <div className={sharedStyles.sectionContainer}>
        <div className={cx(sharedStyles.headlineContainer, sharedStyles.descriptionContainer)}>
          <Text type="brandon" isStrong className={cx(sharedStyles.cssTitle, sharedStyles.titleContainer)}>
            Portfolio
          </Text>
          <Text type="brandon" className={sharedStyles.descriptionText}>
            Leave blank if property does not belong to a portfolio.
          </Text>
        </div>

        <Select
          name="portfolio"
          control={control}
          label={() => 'Select Portfolio'}
          options={handledPortfolios}
          isLoading={portfoliosQueryRes.isFetching}
          optionToString
          blurInputOnSelect
          editable
          creatable
          isClearable
        />
      </div>

      <GoogleMapsAdapter<types.PropertyFormValues> {...methods} />
    </div>
  );
}

export { PropertyForm };
